import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, User, ArrowRight, ShieldCheck, Phone, CheckCircle, Sparkles, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import API from '../config/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  const { register } = useAuth();

  const [loading, setLoading] = useState(false);
  const [registeredSuccess, setRegisteredSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [tempPasswordDev, setTempPasswordDev] = useState('');
  
  const handleGoogleLogin = () => {
    window.location.href = `${API}/auth/google`;
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please provide your name and email address');
      return;
    }

    setLoading(true);

    // Call register API
    const result = await register(formData.email.trim(), '', formData.name.trim(), formData.phone.trim());
    setLoading(false);

    if (result.success) {
      setRegisteredEmail(formData.email.trim());
      if (result.user?.temp_password) {
        setTempPasswordDev(result.user.temp_password);
      }
      setRegisteredSuccess(true);
      toast.success(result.message || 'Account created! Welcome email dispatched.');
    } else {
      toast.error(result.error || 'Registration failed');
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a1128]">
      {/* Background Image & Effects */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1577516311194-eb14c570a137?q=80&w=2000"
          alt="Himalayan Mountains"
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1128]/95 via-[#0a1128]/85 to-[#0a1128]/95 backdrop-blur-sm"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FF7B00]/15 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/15 rounded-full blur-[120px] pointer-events-none"></div>
      </div>

      {/* Main Container */}
      <div className="relative z-10 w-full max-w-[1050px] px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-10 py-12 items-center justify-center">
        
        {/* Left Side Content */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden md:flex flex-col flex-1 max-w-md pr-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-md mb-6 w-max">
            <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse"></span>
            <span className="text-[#D4AF37] font-bold text-xs uppercase tracking-widest">Paramparik Pooja Portal</span>
          </div>
          
          <h1 className="font-heading text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
            Begin Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF7B00] to-[#D4AF37]">Spiritual Path</span>
          </h1>
          
          <p className="text-gray-300 text-base leading-relaxed mb-8">
            Create an account to book authentic temple pujas, receive blessed Himalayan prasad, and enjoy effortless checkout on sacred products.
          </p>

          <div className="space-y-4">
            {[
              { title: 'Welcome Access & Temporary Key', desc: 'Secure temporary password sent to your email instantly' },
              { title: 'Direct Himalayan Temple Offerings', desc: 'Authentic rituals performed with proper Sankalpa' },
              { title: 'Live Darshan & Video Recordings', desc: 'Watch your rituals performed in high resolution' }
            ].map((feature, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 border border-white/10 text-[#D4AF37]">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm">{feature.title}</h4>
                  <p className="text-xs text-gray-400">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right Side Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="w-full max-w-md bg-white/10 backdrop-blur-2xl rounded-[2.5rem] border border-white/20 p-8 sm:p-10 shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
        >
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Link to="/">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF7B00] to-[#D4AF37] flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                <span className="text-white font-heading font-black text-2xl">प</span>
              </div>
            </Link>
          </div>

          <AnimatePresence mode="wait">
            {!registeredSuccess ? (
              <motion.div
                key="register-form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center mb-6">
                  <h2 className="font-heading text-3xl font-black text-white mb-1">Create Account</h2>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    A welcome email with your temporary password will be sent to you.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-gray-300 font-medium text-xs uppercase tracking-wider ml-1">
                      Full Name *
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                        <User className="h-4 w-4" />
                      </div>
                      <Input
                        id="name"
                        type="text"
                        placeholder="e.g. Rameshwar Sharma"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-11 bg-black/30 border-white/10 text-white placeholder-gray-500 rounded-xl h-12 text-sm focus:border-[#D4AF37]/50"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-gray-300 font-medium text-xs uppercase tracking-wider ml-1">
                      Email Address *
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                        <Mail className="h-4 w-4" />
                      </div>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-11 bg-black/30 border-white/10 text-white placeholder-gray-500 rounded-xl h-12 text-sm focus:border-[#D4AF37]/50"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-gray-300 font-medium text-xs uppercase tracking-wider ml-1">
                      Phone Number (Optional)
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                        <Phone className="h-4 w-4" />
                      </div>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+91 9876543210"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-11 bg-black/30 border-white/10 text-white placeholder-gray-500 rounded-xl h-12 text-sm focus:border-[#D4AF37]/50"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-amber-500/10 border border-amber-400/20 rounded-xl text-amber-200 text-xs flex items-start gap-2">
                    <Key className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>
                      You will receive a temporary password in your welcome email, and will be prompted to set your private password upon login.
                    </span>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#FF7B00] to-[#D4AF37] text-white hover:from-[#FF7B00] hover:to-[#eec541] rounded-xl h-12 text-base font-bold shadow-[0_8px_32px_rgba(255,123,0,0.3)] hover:-translate-y-0.5 transition-all mt-2"
                  >
                    {loading ? 'Creating account...' : 'Sign Up & Send Password'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative my-5 text-center">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <span className="relative bg-[#0d1633] px-3 text-xs font-medium uppercase tracking-wider text-gray-400">
                    Or sign up with
                  </span>
                </div>

                {/* Google Sign Up Button */}
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full h-12 flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-800 font-semibold text-sm rounded-xl shadow-md transition-all active:scale-[0.99] cursor-pointer"
                  data-testid="google-signup-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                  <span>Sign up with Google</span>
                </button>

                <p className="text-center text-gray-400 text-sm mt-6">
                  Already registered?{' '}
                  <Link
                    to={`/login${redirectUrl !== '/dashboard' ? `?redirect=${encodeURIComponent(redirectUrl)}` : ''}`}
                    className="text-[#D4AF37] font-bold hover:underline"
                  >
                    Sign In
                  </Link>
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="register-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-4 space-y-6"
              >
                <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto shadow-inner">
                  <CheckCircle className="w-8 h-8" />
                </div>

                <div>
                  <h3 className="font-heading text-2xl font-bold text-white mb-2">
                    Welcome to Paramparik Pooja!
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    We have sent a welcome email with your <strong>Temporary Password</strong> to:
                  </p>
                  <p className="text-[#D4AF37] font-mono font-bold mt-1 text-sm bg-white/5 py-1 px-3 rounded-lg inline-block border border-white/10">
                    {registeredEmail}
                  </p>
                </div>

                {tempPasswordDev && (
                  <div className="p-3 bg-amber-500/20 border border-amber-400/30 rounded-xl text-amber-200 text-xs">
                    <p className="font-semibold text-amber-300 mb-1">Temporary Password Preview:</p>
                    <code className="font-mono text-sm bg-black/40 px-2 py-0.5 rounded text-white">{tempPasswordDev}</code>
                  </div>
                )}

                <div className="space-y-3 pt-2">
                  <Link
                    to={`/login${redirectUrl !== '/dashboard' ? `?redirect=${encodeURIComponent(redirectUrl)}` : ''}`}
                    className="block"
                  >
                    <Button className="w-full btn-saffron rounded-xl py-6 font-bold text-base shadow-lg">
                      Proceed to Login & Set Password
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
